import React, { useCallback, useContext, useState } from 'react';
import * as customerApi from '../api/customer';
import { useCustomerDetails } from '../hooks/customer/use-customer';
import { Address } from '../interfaces/address';
import { showSuccessToast } from '../utils/toasts';
import { useAuth } from './auth';

interface AddressesContext {
  addresses?: Array<Address>;
  loading: boolean;
  setDefaultAddress: (addressId: number) => Promise<void>;
  createAddress: (address: Address) => Promise<Address>;
  updateAddress: (address: Address) => Promise<void>;
  deleteAddress: (addressId: number) => Promise<void>;
}

const AddressesContext = React.createContext<AddressesContext>(null as any);

export const AddressesContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { customer } = useAuth();
  const { setShippingAddress } = useAuth();
  const {
    details,
    loading: customerLoading,
    refetch: refetchCustomer,
  } = useCustomerDetails(customer?.customerId);

  const [loading, setLoading] = useState(false);
  const addresses = details && details.addresses;
  const setDefaultAddress = useCallback(
    async (addressId: number) => {
      const address = (addresses || []).find((address) => address.id === addressId);
      if (!address) {
        return;
      }
      setLoading(true);

      try {
        if (!customer) {
          throw new Error('no customer data');
        }
        await customerApi.updateAddress(customer.customerId, {
          ...address,
          default: true,
        });

        setShippingAddress({
          ...address,
          default: true,
        });

        await refetchCustomer();

        showSuccessToast({
          message: 'account:addresses:set-default-success',
        });
      } finally {
        setLoading(false);
      }
    },
    [customer, addresses, setShippingAddress, refetchCustomer]
  );

  const createAddress = useCallback(
    async (address: Address) => {
      if (!customer) {
        throw new Error('no customer data');
      }
      const createdAddress = await customerApi.createAddress(customer.customerId, address);
      await refetchCustomer();

      showSuccessToast({
        message: 'account:addresses:create-success',
      });
      return createdAddress;
    },
    [customer, refetchCustomer]
  );

  const updateAddress = useCallback(
    async (address: Address) => {
      if (!customer) {
        throw new Error('no customer data');
      }
      await customerApi.updateAddress(customer.customerId, address);
      if (address.default) setShippingAddress({ ...address, default: true });
      await refetchCustomer();
      showSuccessToast({
        message: 'account:addresses:update-success',
      });
    },
    [customer, setShippingAddress, refetchCustomer]
  );

  const deleteAddress = useCallback(
    async (addressId: number) => {
      if (!customer) {
        throw new Error('no customer data');
      }
      await customerApi.deleteAddress(customer.customerId, addressId);
      await refetchCustomer();
      showSuccessToast({
        message: 'account:addresses:delete-success',
      });
    },
    [customer, refetchCustomer]
  );

  return (
    <AddressesContext.Provider
      value={{
        addresses,
        loading: loading || customerLoading,
        setDefaultAddress,
        createAddress,
        updateAddress,
        deleteAddress,
      }}
    >
      {children}
    </AddressesContext.Provider>
  );
};

export const useAddresses = () => useContext(AddressesContext);
